import React, { useEffect, useRef } from 'react';
import { parseISO, startOfDay } from 'date-fns';
import EventService from '../services/event-service';
import FileStorageService from '../services/file-storage-service';
import { getCurrentDateObject, toScheduledAt } from './date-helper';
import { toInputDate } from './string-format-helper';
import { toStudentUuids } from './student-uuid-helper';

const maxFileSizeInMb = 10;
const maxFileSize = maxFileSizeInMb * 1024 * 1024;

// startsAt/endsAt são horas de parede da escola: o formulário fatia o `local`
// em vez de passar por new Date(), que reinterpretaria no fuso do navegador.
export const wallClockToDate = (local: string): string => local.slice(0, 10);
export const wallClockToHour = (local: string): string => local.slice(11, 16);
const toWallClock = (date: string, hour: string) => `${date}T${hour}:00`;

export const getEventData = (previousData?: EventDto) => {
  const data: EventFormData = {
    formData: {
      title: previousData?.title || null,
      image: previousData?.image?.url || null,
      imageFileId: previousData?.image?.fileId || null,
      description: previousData?.description || null,
      date: previousData ? wallClockToDate(previousData.startsAt.local) : null,
      startHour: previousData ? wallClockToHour(previousData.startsAt.local) : null,
      endHour: previousData ? wallClockToHour(previousData.endsAt.local) : null,
      place: previousData?.place || null,
    },
    classData: [],
    studentData: [],
    unmatchedStudentUuids: [],
    scheduleDate: null,
  };

  return data;
};

export const isFormComplete = (data: EventFormData, validations: object) =>
  Boolean(
    data.formData.title &&
      data.formData.description &&
      data.formData.date &&
      data.formData.startHour &&
      data.formData.endHour &&
      data.formData.place &&
      (data.studentData.length > 0 || data.unmatchedStudentUuids.length > 0) &&
      Object.keys(validations).length === 0
  );

type SetData = React.Dispatch<React.SetStateAction<EventFormData>>;
type SetValidations = React.Dispatch<React.SetStateAction<object>>;

const setValidation = (setValidations: SetValidations, id: string, message: string | null) =>
  setValidations((current) => {
    const next = { ...current };
    if (message) next[id] = message;
    else delete next[id];
    return next;
  });

// A prévia da imagem recém-enviada é uma blob: URL, que prende o arquivo inteiro
// na memória até ser revogada — e o SPA nunca descarrega a página.
const revokePreviewUrl = (url: string | null) => {
  if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
};

export const useRevokePreviewUrlOnUnmount = (data: EventFormData) => {
  const image = useRef<string | null>(null);
  image.current = data.formData.image;

  useEffect(() => () => revokePreviewUrl(image.current), []);
};

export const forceRemovePreviewImage = (setData: SetData) => {
  setData((current) => {
    revokePreviewUrl(current.formData.image);

    return { ...current, formData: { ...current.formData, image: null, imageFileId: null } };
  });
};

export const handleStudentChange = (
  selectedClassrooms: number[],
  selectedStudents: number[],
  setData: SetData
) => {
  setData((current) => ({
    ...current,
    classData: selectedClassrooms,
    studentData: selectedStudents,
  }));
};

export interface FormChangeContext {
  data: EventFormData;
  setData: SetData;
  setValidations: SetValidations;
  setIsLoadingImage: React.Dispatch<React.SetStateAction<boolean>>;
  fileStorageService: FileStorageService;
  setToastContent: (content: ToastContent) => void;
}

const uploadImage = async (
  file: File,
  { setData, setValidations, setIsLoadingImage, fileStorageService, setToastContent }: FormChangeContext
) => {
  if (file.size > maxFileSize) {
    setValidation(setValidations, 'image', `O tamanho máximo da imagem é ${maxFileSizeInMb}MB.`);
    return;
  }

  setValidation(setValidations, 'image', null);
  setIsLoadingImage(true);

  try {
    const fileId = await fileStorageService.uploadFile(file);

    // A API devolve a url assinada só depois do upload no S3 terminar; até
    // lá usamos um preview local para não travar a UX no formulário.
    const preview = URL.createObjectURL(file);
    setData((current) => {
      revokePreviewUrl(current.formData.image);

      return { ...current, formData: { ...current.formData, image: preview, imageFileId: fileId } };
    });
  } catch {
    setToastContent({ isOpen: true, text: 'Houve um erro ao enviar a imagem.', type: 'error' });
  } finally {
    setIsLoadingImage(false);
  }
};

const dateValidation = (date: string): string | null => {
  if (!date) return 'Campo obrigatório.';
  if (parseISO(date).getTime() < getCurrentDateObject().getTime())
    return 'Não é possível cadastrar eventos para datas passadas.';

  return null;
};

// "HH:mm" compara corretamente como texto.
const setHourValidations = (startHour: string, endHour: string, setValidations: SetValidations) => {
  if (startHour && endHour && endHour < startHour) {
    setValidation(setValidations, 'startHour', 'O horário deve ser menor que o de término.');
    setValidation(setValidations, 'endHour', 'O horário deve ser maior que o de início.');
    return;
  }

  setValidation(setValidations, 'startHour', startHour ? null : 'Campo obrigatório.');
  setValidation(setValidations, 'endHour', endHour ? null : 'Campo obrigatório.');
};

// Toda atualização de estado aqui é funcional: o upload leva tempo, e o que foi
// digitado ou selecionado nesse meio-tempo não pode ser sobrescrito pelo estado
// de quando o arquivo foi escolhido.
export const handleFormOnChange = async (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  context: FormChangeContext
) => {
  const { id, value, type } = e.target;

  if (type !== 'file') {
    if (id === 'startHour') setHourValidations(value, context.data.formData.endHour, context.setValidations);
    else if (id === 'endHour') setHourValidations(context.data.formData.startHour, value, context.setValidations);
    else setValidation(context.setValidations, id, id === 'date' ? dateValidation(value) : value ? null : 'Campo obrigatório.');

    context.setData((current) => ({ ...current, formData: { ...current.formData, [id]: value } }));
    return;
  }

  // O Form também chama no blur; limpar o input deixa escolher o mesmo arquivo de novo.
  if (e.type === 'blur') {
    e.target.value = null;
    return;
  }

  if (!value) return;

  await uploadImage((e.target as HTMLInputElement).files[0], context);
};

const handleScheduleDateSelect = (
  evt: React.ChangeEvent<HTMLInputElement>,
  data: EventFormData,
  setData: SetData,
  submitPromise: (submitData: EventFormData) => Promise<string | void>,
  setModalContent: (content: ModalContent) => any
) => {
  const scheduleDate = evt.target.value ? toScheduledAt(evt.target.value) : null;

  setData((current) => ({ ...current, scheduleDate }));

  createSetModalContent({ ...data, scheduleDate }, submitPromise, setData, setModalContent);
};

export const createSetModalContent = (
  data: EventFormData,
  submitPromise: (submitData: EventFormData) => Promise<string | void>,
  setData: SetData,
  setModalContent: (content: ModalContent) => any
) => {
  const submitSchedulePromise = (submitData: EventFormData) => {
    if (!submitData.scheduleDate) {
      return Promise.reject('Selecione uma data para o agendamento.');
    }

    const selectDate = startOfDay(parseISO(submitData.scheduleDate)).getTime();
    const currentDate = getCurrentDateObject().getTime();
    const eventDate = parseISO(submitData.formData.date).getTime();

    if (selectDate < currentDate) {
      return Promise.reject('Não é possível agendar eventos para datas passadas.');
    } else if (selectDate >= eventDate) {
      return Promise.reject('O Agendamento deve ocorrer ao menos um dia antes do evento.');
    } else {
      return submitPromise(submitData);
    }
  };

  const resetScheduleDate = () => {
    setData((current) => ({ ...current, scheduleDate: null }));

    return Promise.resolve();
  };

  setModalContent({
    title: 'Selecione uma data para agendar o envio',
    modalContent: React.createElement('input', {
      type: 'date',
      name: 'schedule',
      id: 'schedule',
      min: toInputDate(new Date()),
      max: '2999-12-31',
      onChange: (evt) => handleScheduleDateSelect(evt, data, setData, submitPromise, setModalContent),
    }),
    submitButtonTitle: 'agendar',
    submitButtonType: 'primary',
    submitData: data,
    onSubmitButtonClick: submitSchedulePromise,
    onCloseModal: resetScheduleDate,
    isOpen: true,
  });
};

export const deleteEvent = (
  eventService: EventService,
  eventId: string,
  setModalContent: (content: ModalContent) => any,
  setToastContent: (content: ToastContent) => void,
  navigate: any
) => {
  setModalContent({
    title: 'Deletar evento',
    modalContent: React.createElement('p', null, 'Deseja realmente deletar o evento?'),
    submitButtonTitle: 'confirmar deleção',
    submitButtonType: 'primary',
    submitData: null,
    onSubmitButtonClick: () =>
      eventService
        .deleteEvent(eventId)
        .then(() => {
          setToastContent({
            isOpen: true,
            text: 'Evento deletado!',
            type: 'success',
          });
          navigate('/eventos/listar');
        })
        .catch(() => {
          setToastContent({
            isOpen: true,
            text: 'Houve um erro ao deletar o evento.',
            type: 'error',
          });
        }),
    isOpen: true,
  });
};

export const buildEventRequest = (data: EventFormData, classroomList: ClassroomStudents[]): EventRequest => ({
  title: data.formData.title,
  description: data.formData.description,
  place: data.formData.place,
  startsAt: toWallClock(data.formData.date, data.formData.startHour),
  endsAt: toWallClock(data.formData.date, data.formData.endHour),
  scheduledAt: data.scheduleDate || null,
  imageFileId: data.formData.imageFileId || null,
  studentUuids: toStudentUuids(data.studentData, data.unmatchedStudentUuids, classroomList),
});

const responseLabel = (response?: EventResponse): string => {
  if (response === 'Accepted') return 'Aceito';
  if (response === 'Declined') return 'Recusado';

  return 'Não respondido';
};

// O BFF resolve os nomes no servidor. `seen` é cumulativo (quem respondeu
// também viu), então as duas colunas são independentes.
export const backofficeRecipientsToEngagementRows = (
  recipients: BackofficeEventRecipientDto[]
): InfosDataListRow[][] =>
  recipients.map((recipient) => [
    { text: recipient.student.name || '—' },
    { text: recipient.classroom?.name || '—' },
    { text: recipient.responsible?.name || '—' },
    {
      text: recipient.seen ? 'Sim' : 'Não',
      customStyle: { textAlign: 'center' },
    },
    {
      text: responseLabel(recipient.response),
      customStyle: { textAlign: 'center' },
    },
  ]);
